import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  Film, 
  Sparkles, 
  Users, 
  FileText, 
  Zap, 
  CheckCircle,
  ArrowRight,
  Play,
  ChevronRight,
  Trophy
} from 'lucide-react';
import AnimatedStepsDemo from '../components/AnimatedStepsDemo';
import { AnimatedCounter, FilmReelParticles } from '../components/AnimatedEffects';

const Landing = () => {
  const { user } = useAuth();
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const features = [
    {
      id: 'structure',
      icon: Film,
      title: '3-Act Structure',
      description: 'Build your story with the proven Setup, Confrontation, and Resolution framework used by Hollywood professionals.'
    },
    {
      id: 'ai',
      icon: Sparkles,
      title: 'AI Co-Writer',
      description: 'Get intelligent suggestions for dialogue, plot directions, and character actions. Always choose between 2 creative options.'
    },
    {
      id: 'collab',
      icon: Users,
      title: 'Collaboration',
      description: 'Work with your writing partners in real-time. Comments, version history, and seamless teamwork.'
    },
    {
      id: 'format',
      icon: FileText,
      title: 'Pro Formatting',
      description: 'Export industry-standard screenplays ready for Final Draft, Celtx, or any professional software.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Film className="w-8 h-8 text-primary" />
            <span className="font-black text-xl tracking-tight">MOVIEBOT</span>
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider">Features</a>
            <Link to="/challenge" className="text-sm font-medium text-teal-400 hover:text-teal-300 transition-colors uppercase tracking-wider flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              Summer Competition
            </Link>
            <Link to="/bollywood" className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors uppercase tracking-wider">Bollywood</Link>
            <Link to="/nollywood" className="text-sm font-medium text-green-400 hover:text-green-300 transition-colors uppercase tracking-wider">Nollywood</Link>
            <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider">Contact</Link>
          </div>

          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-6 py-2" data-testid="dashboard-btn">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-muted-foreground hover:text-white text-xs uppercase tracking-wider" data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs px-6 py-2 btn-glow" data-testid="get-started-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Competition Banner - Under Nav */}
      <Link 
        to="/challenge" 
        className="fixed top-[72px] left-0 right-0 z-40 bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 py-2.5 px-4 cursor-pointer hover:from-teal-500 hover:via-teal-400 hover:to-emerald-400 transition-all group"
      >
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHoiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBzdHJva2Utd2lkdGg9IjIiLz48L2c+PC9zdmc+')] opacity-30" />
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 relative">
          <div className="flex items-center gap-3 text-center md:text-left">
            <Trophy className="w-5 h-5 text-yellow-300 flex-shrink-0 hidden sm:block" />
            <div>
              <span className="font-black text-white text-sm md:text-base">
                Your screenplay. Produced. On streaming.
              </span>
              <span className="hidden md:inline text-white/80 text-sm ml-2">
                — The MovieBot Summer Film Competition is open.
              </span>
            </div>
          </div>
          
          <span className="bg-white text-teal-600 font-bold text-xs px-3 py-1 rounded group-hover:bg-yellow-300 group-hover:text-black transition-colors flex items-center gap-1">
            Enter the Competition — Free
            <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-36 md:pt-32">
        {/* Background */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1634063575144-f513e083e625?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.15) 0%, rgba(5, 5, 5, 0) 70%)'
          }}
        />
        
        {/* Floating film reel particles */}
        <FilmReelParticles count={15} />

        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Text content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-none mb-6">
                Netflix Wants<br />
                <span className="text-primary">Your Script.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-8">
                So does Prime, Tubi, and every studio in Hollywood. Write your screenplay with AI-powered guidance and turn your movie idea into reality.
              </p>
              
              {/* Demand Stats - Animated */}
              <div className="grid grid-cols-3 gap-4 max-w-lg mb-10">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-4 bg-white/5 border border-white/10 backdrop-blur-sm text-center"
                >
                  <p className="text-2xl md:text-3xl font-black text-primary">
                    $<AnimatedCounter end={200} duration={2} suffix="B+" />
                  </p>
                  <p className="text-xs text-muted-foreground">spent yearly on content</p>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="p-4 bg-white/5 border border-white/10 backdrop-blur-sm text-center"
                >
                  <p className="text-2xl md:text-3xl font-black text-primary">
                    <AnimatedCounter end={10000} duration={2.5} suffix="+" />
                  </p>
                  <p className="text-xs text-muted-foreground">scripts needed/year</p>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="p-4 bg-white/5 border border-white/10 backdrop-blur-sm text-center"
                >
                  <p className="text-2xl md:text-3xl font-black text-primary">
                    <AnimatedCounter end={90} duration={1.5} /> min
                  </p>
                  <p className="text-xs text-muted-foreground">to write first draft</p>
                </motion.div>
              </div>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <Link to={user ? "/dashboard" : "/signup"}>
                  <Button 
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-sm px-10 py-6 btn-glow flex items-center gap-2"
                    data-testid="hero-cta-btn"
                  >
                    <Play className="w-5 h-5" />
                    Start Writing Free
                  </Button>
                </Link>
                <a href="#features">
                  <Button 
                    variant="outline" 
                    className="border-white/20 text-white hover:bg-white/10 hover:border-white/40 font-bold uppercase tracking-widest text-sm px-10 py-6"
                    data-testid="learn-more-btn"
                  >
                    Learn More
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Right side - Hero Visual */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden border-2 border-primary/30">
                <img 
                  src="https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80"
                  alt="Film Production"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                
                {/* Floating Script Preview */}
                <div className="absolute bottom-6 left-6 right-6 bg-black/90 backdrop-blur border border-primary/30 p-4 rounded">
                  <p className="text-xs text-primary font-mono mb-2">SCENE 1 - INT. COFFEE SHOP - DAY</p>
                  <p className="text-sm text-white/80 font-mono">
                    ALEX stares at the blank laptop screen. The cursor blinks mockingly. Then—an idea. A smile spreads across their face.
                  </p>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-primary/30 rounded-full" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary rounded-full blur-xl opacity-50" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Competition Section - Between Hero and Features */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/30 via-teal-800/20 to-emerald-900/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(20,184,166,0.15)_0%,_transparent_70%)]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-500/30 px-4 py-2 mb-6">
              <Trophy className="w-5 h-5 text-teal-400" />
              <span className="text-teal-400 font-bold text-sm uppercase tracking-wider">Summer Film Competition</span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Your screenplay. <span className="text-teal-400">Produced.</span> On streaming.
            </h2>
            
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The MovieBot Summer Film Competition is open. Write your short film screenplay. We produce the winner and submit it to streaming platforms.
            </p>

            {/* Summer 2026 Badge */}
            <div className="inline-flex items-center gap-3 bg-teal-500/10 border border-teal-500/30 px-6 py-4 mb-10">
              <Sparkles className="w-6 h-6 text-teal-400" />
              <div className="text-left">
                <p className="text-xs text-teal-400 font-mono uppercase tracking-wider">Open Now</p>
                <p className="text-xl font-black text-white">Summer 2026</p>
              </div>
            </div>

            <div className="block">
              <Link to="/challenge">
                <Button 
                  size="lg"
                  className="bg-teal-500 text-white hover:bg-teal-400 font-bold text-lg px-10 py-6"
                >
                  <Trophy className="w-5 h-5 mr-2" />
                  Enter the Competition — Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Everything You Need to Write
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Professional tools designed for aspiring screenwriters, film students, and indie filmmakers.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredFeature(feature.id)}
                onMouseLeave={() => setHoveredFeature(null)}
                className={`group relative overflow-hidden bg-secondary/30 border border-white/5 p-8 transition-all duration-500 ${
                  hoveredFeature === feature.id ? 'border-primary/50' : ''
                }`}
                data-testid={`feature-${feature.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 transition-colors duration-300 ${
                    hoveredFeature === feature.id ? 'bg-primary text-black' : 'bg-white/5 text-primary'
                  }`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Animated Demo */}
      <section className="py-24 bg-secondary/20">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              From Idea to Script in <span className="text-primary">4 Simple Steps</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              No screenwriting experience needed. If you can describe your movie, you can write it.
            </p>
          </motion.div>

          <AnimatedStepsDemo accentColor="primary" demoStyle="hollywood" />

          <div className="text-center mt-12">
            <Link to={user ? "/dashboard" : "/signup"}>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-sm px-10 py-6 btn-glow"
                data-testid="steps-cta-btn"
              >
                Start Writing Free — It Takes 10 Minutes
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <p className="text-sm text-muted-foreground mt-4">Act 1 is completely free. No credit card required.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1623658877772-69ae99a89304?w=1920&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505]" />
        
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Zap className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
              Ready to Write Your Story?
            </h2>
            <p className="text-muted-foreground text-lg mb-10">
              Join thousands of screenwriters who are turning their ideas into reality.
            </p>
            <Link to={user ? "/dashboard" : "/signup"}>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-sm px-12 py-6 btn-glow"
                data-testid="final-cta-btn"
              >
                Start Writing Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Film className="w-6 h-6 text-primary" />
              <span className="font-bold tracking-tight">MOVIEBOT</span>
            </div>
            <div className="flex items-center gap-6 flex-wrap justify-center">
              <a href="#features" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Features
              </a>
              <Link to="/challenge" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Summer Competition
              </Link>
              <Link to="/bollywood" className="text-sm text-orange-400 hover:text-orange-300 transition-colors">
                Bollywood
              </Link>
              <Link to="/nollywood" className="text-sm text-green-400 hover:text-green-300 transition-colors">
                Nollywood
              </Link>
              <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Contact Us
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2026 MovieBot. Built for storytellers.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
